import { publicCatalogApi } from '@/features/publicsite/catalog/api/publicCatalogApi';
import { CatalogCard } from '@/features/publicsite/catalog/CatalogCard';
import { CatalogSection } from '@/features/publicsite/catalog/CatalogSection';
import type { Program } from '@/features/publicsite/types/funding/Program';
import { usePublicList } from '@/features/publicsite/usePublicList';
import { domainLabel, useThematicDomains } from '@/features/common/useThematicDomains';
import { websiteHref } from '@/features/common/organizationProfile';
import { useLanguage } from '@/i18n/LanguageContext';
import { publicCatalog, type PublicCatalogKey } from '@/i18n/dictionaries/publicCatalog';
import { useTranslation } from '@/i18n/useTranslation';

const SPONSOR_BADGE_KEY: Record<Program['sponsorType'], PublicCatalogKey> = {
  GOVERNMENTAL: 'agenciesSponsorGovernmental',
  INTERNATIONAL: 'agenciesSponsorInternational',
  PARTNER: 'agenciesSponsorPartner',
};

/** ToR §6.9: governmental, international and development-partner programs supporting the sector. */
export function SupportAgenciesPage() {
  const { t } = useTranslation(publicCatalog);
  const { language } = useLanguage();
  const programs = usePublicList<Program>(publicCatalogApi.programsUrl);
  // The same nomenclature Measure.domains resolves against — support_program.thematic_areas
  // holds ids into it too.
  const domains = useThematicDomains();

  const areasOf = (p: Program) =>
    p.thematicAreas
      .map((id) => domains.find((d) => d.id === id))
      .filter((d): d is NonNullable<typeof d> => Boolean(d))
      .map((d) => domainLabel(d, language))
      .join(', ');

  return (
    <CatalogSection
      eyebrow={t('eyebrowCatalog')}
      heading={t('agenciesHeading')}
      lead={t('agenciesLead')}
      items={programs}
      renderCard={(p) => {
        const areas = areasOf(p);
        return (
          <CatalogCard
            key={p.id}
            badge={t(SPONSOR_BADGE_KEY[p.sponsorType])}
            title={p.title}
            subtitle={p.supportTypes}
            href={websiteHref(p.website) ?? undefined}
            linkLabel={p.website ?? undefined}
          >
            {p.objectives && <p>{p.objectives}</p>}
            {areas && <p className="muted">{t('agenciesThematicAreas', { areas })}</p>}
          </CatalogCard>
        );
      }}
    />
  );
}
